import { HamburgerIcon } from '@chakra-ui/icons';
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerOverlay,
	VStack,
	HStack,
	Icon,
	List,
	ListItem,
	Stack,
	Text,
	Image,
	IconButton,
	useColorModeValue,
	useDisclosure,
	useColorMode,
} from "@chakra-ui/react";
import IconBox from 'components/Icons/IconBox';
import { HSeparator } from 'components/Separator/Separator';
import React, { useContext, useMemo, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { FaCircle } from 'react-icons/fa';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import SidebarDocs from "components/Sidebar/SidebarDocs";
import { SidebarContext } from "contexts/SidebarContext";
import { Scrollbars } from "react-custom-scrollbars-2";
import NortewareLogo from "assets/img/norteware-logo.png";
import { ROUTES } from 'constants/routes-path';
import {
	CUSTOMER_LOGO,
	CUSTOMER_LOGO_DARK,
	CUSTOMER_LOGO_SECONDARY,
	CUSTOMER_LOGO_SECONDARY_DARK,
	SHOW_SECONDARY_LOGO_IN_SIDEBAR,
} from "constants/config";

export function SidebarResponsive(props) {
	const { display, routes } = props;
	const { t } = useTranslation();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { colorMode } = useColorMode();
	const { routeStack, displayDocs } = useContext(SidebarContext);
	const btnRef = useRef(undefined);
	const location = useLocation();
	const navigate = useNavigate();

	const inactiveBg = useColorModeValue('transparent', 'navy.700');
	const activeColor = useColorModeValue('gray.700', 'white');
	const inactiveColor = useColorModeValue('gray.400', 'gray.400');
	const activeAccordionBg = useColorModeValue('white', 'navy.700');
	const sidebarActiveShadow = useColorModeValue('0px 7px 11px rgba(0, 0, 0, 0.04)', 'none');
	const sidebarBackgroundColor = useColorModeValue('white', 'navy.900');
	const activeBg = "blue.500";
	const hamburgerColor = "white";
	const activeColorIcon = "white";
	const inactiveColorIcon = "blue.500";
	const variantChange = "0.2s linear";

	const handleClick = () => {
		navigate(ROUTES.trafficViolationsPath);
	}

	const getLocalizedName = (name) => {
		const localized = name.includes(".title") ? t(name) : name;
		return localized.trim();
	}

	const activeRoute = (route) => {
		if (routeStack?.length) {
			return routeStack.some(e => e.path === route.path || e.name === route.name);
		}

		return location.pathname.includes(route.path);
	};

	const linkContent = (route, isActive) => {
		return (
			<HStack gap="12px">
				{route.icon ? (
					<IconBox
						h='30px'
						w='30px'
						flexShrink={0}
						bg={isActive ? activeBg : inactiveBg}
						color={isActive ? activeColorIcon : inactiveColorIcon}
						transition={variantChange}
					>
						{route.icon}
					</IconBox>
				) : (
					<IconBox h='30px' w='30px'>
						<Icon
							w={isActive ? '10px' : '6px'}
							h={isActive ? '10px' : '6px'}
							flexShrink={0}
							color={inactiveColorIcon}
							transition={variantChange}
							as={FaCircle}
						/>
					</IconBox>
				)}

				<Text
					fontSize='sm'
					color={isActive ? activeColor : inactiveColor}
					fontWeight={isActive ? 'bold' : 'normal'}
				>
					{getLocalizedName(route.name)}
				</Text>
			</HStack>
		);
	}

	const createLinks = (routes) => {
		return routes.map((prop, key) => {
			const isActive = activeRoute(prop);

			if (prop.category) {
				return (
					<Box key={key}>
						<Text
							key={key}
							fontSize='md'
							fontWeight='bold'
							mx='auto'
							py='12px'
							ps={{ sm: '10px', xl: '16px' }}
							color={activeColor}
						>
							{getLocalizedName(prop.name)}
						</Text>

						{createLinks(prop.items, isActive)}
					</Box>
				);
			}

			if (prop.collapse) {
				return (
					<Accordion key={key} allowToggle defaultIndex={isActive ? 0 : -1}>
						<AccordionItem border='none'>
							<AccordionButton
								key={key}
								p="12px"
								borderRadius='8px'
								boxShadow={isActive && prop.icon ? sidebarActiveShadow : 'none'}
								bg={isActive && prop.icon ? activeAccordionBg : 'transparent'}
							>
								<HStack w="100%" align='center' justify="space-between">
									{linkContent(prop, isActive)}

									<AccordionIcon color='gray.400' />
								</HStack>
							</AccordionButton>

							<AccordionPanel>
								<List>
									{prop.icon ? (
										createLinks(prop.items)
									) : (
										createAccordionLinks(prop.items)
									)
									}
								</List>
							</AccordionPanel>
						</AccordionItem>
					</Accordion>
				);
			}

			if (prop.url) {
				return (
					<a key={key} href={prop.url} target="_blank" rel="noopener noreferrer" onClick={onClose}>
						<HStack key={key} p="12px" borderRadius="8px" align="center">
							{linkContent(prop, isActive)}
						</HStack>
					</a>
				);
			}

			return (
				<NavLink key={key} to={prop.layout + prop.path} onClick={onClose}>
					<HStack key={key} p="12px" borderRadius='8px' align="center">
						{linkContent(prop, isActive)}
					</HStack>
				</NavLink>
			);
		});
	};

	const createAccordionLinks = (routes) => {
		return routes.map((prop, key) => {
			const isActive = activeRoute(prop);

			return (
				<NavLink key={key} to={prop.layout + prop.path} onClick={onClose}>
					<ListItem pt='5px' ms='26px' key={key}>
						<Text
							fontSize='sm'
							color={isActive ? activeColor : inactiveColor}
							fontWeight={isActive ? 'bold' : 'normal'}
						>
							{getLocalizedName(prop.name)}
						</Text>
					</ListItem>
				</NavLink>
			);
		});
	};

	const navbarLinks = useMemo(() => createLinks(routes), [location, colorMode]);

	const logo = useMemo(() => {
		return props.logo ?? (
			<Stack direction="row" spacing="12px" align="center" justify="center">
				<Image
					maxH="100px"
					alt="Logo 2"
					objectFit="contain"
					src={ colorMode === "dark" ? CUSTOMER_LOGO_DARK : CUSTOMER_LOGO }
					w={ SHOW_SECONDARY_LOGO_IN_SIDEBAR ? "100px" : "200px" }
					h={ SHOW_SECONDARY_LOGO_IN_SIDEBAR ? "100px" : "200px" }
					onClick={() => handleClick()}
					cursor="pointer"
				/>
				<Box w="1px" h="20px" bg={colorMode === "dark" ? "white" : "gray.700"} />

				{SHOW_SECONDARY_LOGO_IN_SIDEBAR && (colorMode === "dark" ? (
					<Image
						maxH="100px"
						alt="Logo 1"
						w="100px"
						h="100px"
						objectFit="contain"
						src={ CUSTOMER_LOGO_SECONDARY_DARK || NortewareLogo }
						onClick={() => handleClick()}
						cursor="pointer"
					/>
				) : (
					<Image
						maxH="100px"
						alt="Logo 1"
						w="100px"
						h="100px"
						objectFit="contain"
						src={ CUSTOMER_LOGO_SECONDARY || NortewareLogo }
						onClick={() => handleClick()}
						cursor="pointer"
					/>
				))}
			</Stack>
		);
	}, [props.logo, colorMode]);

	return (
		<Box display={display}>
			<Box display="flex">
				<IconButton
					p="8px"
					variant="no-effects"
					aria-label="Open sidebar"
					color={hamburgerColor}
					icon={<HamburgerIcon w="22px" h="22px" style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.7))" }} />}
					onClick={onOpen}
				/>

				<Drawer
					isOpen={isOpen}
					onClose={onClose}
					finalFocusRef={btnRef}
					placement={document.documentElement.dir === 'rtl' ? 'right' : 'left'}
				>
					<DrawerOverlay />

					<DrawerContent
						maxW='250px'
						borderRadius='16px'
						py="16px"
						ms={{ sm: '16px' }}
						my={{ sm: '16px' }}
						bg={sidebarBackgroundColor}
					>
						<DrawerCloseButton zIndex="2" bg="transparent" _focus={{ boxShadow: "none" }} _hover={{ boxShadow: "none" }} />

						<Scrollbars autoHide>
							<DrawerBody>
								<VStack w="100%" align="center">
									<Box>
										{logo}

										<HSeparator />
									</Box>

									<Box w="100%">
										<Stack gap="0" direction='column'>
											{navbarLinks}
										</Stack>
									</Box>

									<Box w="100%">
										{displayDocs && <SidebarDocs />}
									</Box>
								</VStack>
							</DrawerBody>
						</Scrollbars>
					</DrawerContent>
				</Drawer>
			</Box>
		</Box>
	);
}

export default SidebarResponsive;
